import * as SheetPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";

const Sheet = SheetPrimitive.Root;

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn(
      "fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
    ref={ref}
  />
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

const sheetVariants = cva(
  "fixed z-50 gap-4 bg-background p-6 shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
  {
    variants: {
      side: {
        top: "inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom:
          "inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        left: "inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
        right:
          "inset-y-0 right-0 h-full w-3/4  border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm",
      },
    },
    defaultVariants: {
      side: "right",
    },
  },
);

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {
  /** @deprecated use `mobileBackHeader` instead. The desktop X is always shown. */
  hideClose?: boolean;
  /** When true, the X close affordance is hidden on mobile (<md) only — useful when the sheet provides its own back-arrow header for thumb reach. Desktop ALWAYS shows the X. */
  mobileBackHeader?: boolean;
  /** Enables the standard mobile bottom-sheet grabber. Defaults to true for bottom sheets. */
  dragToClose?: boolean;
}

const SheetContent = React.forwardRef<React.ElementRef<typeof SheetPrimitive.Content>, SheetContentProps>(
  ({ side = "right", hideClose = false, mobileBackHeader = false, dragToClose, className, children, ...props }, ref) => {
    // Desktop ALWAYS shows the X. Mobile hides it when the sheet provides its
    // own back-arrow header (mobileBackHeader) OR legacy callers passed hideClose.
    const hideOnMobile = mobileBackHeader || hideClose;
    const closeRef = React.useRef<HTMLButtonElement>(null);
    const dragStartYRef = React.useRef<number | null>(null);
    const dragLastYRef = React.useRef<number | null>(null);
    const shouldShowDragHandle = side === "bottom" && dragToClose !== false;

    const closeFromDrag = React.useCallback(() => {
      closeRef.current?.click();
    }, []);

    const startDrag = React.useCallback((clientY: number) => {
      dragStartYRef.current = clientY;
      dragLastYRef.current = clientY;
    }, []);

    const moveDrag = React.useCallback((clientY: number) => {
      if (dragStartYRef.current === null) return;
      dragLastYRef.current = clientY;
    }, []);

    const endDrag = React.useCallback(() => {
      const startY = dragStartYRef.current;
      const lastY = dragLastYRef.current;
      dragStartYRef.current = null;
      dragLastYRef.current = null;
      if (startY === null || lastY === null) return;
      if (lastY - startY > 56) closeFromDrag();
    }, [closeFromDrag]);

    return (
      <SheetPortal>
        <SheetOverlay />
        <SheetPrimitive.Content ref={ref} className={cn(sheetVariants({ side }), className)} {...props}>
          {shouldShowDragHandle && (
            <div className="flex shrink-0 items-center justify-center pb-1 pt-1 md:hidden">
              <button
                type="button"
                className="h-7 w-28 touch-none rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                aria-label="Close"
                onClick={closeFromDrag}
                onTouchStart={(event) => startDrag(event.touches[0]?.clientY ?? 0)}
                onTouchMove={(event) => moveDrag(event.touches[0]?.clientY ?? 0)}
                onTouchEnd={endDrag}
                onTouchCancel={endDrag}
                onPointerDown={(event) => {
                  if (event.pointerType !== "touch") return;
                  startDrag(event.clientY);
                }}
                onPointerMove={(event) => {
                  if (event.pointerType !== "touch") return;
                  moveDrag(event.clientY);
                }}
                onPointerUp={(event) => {
                  if (event.pointerType !== "touch") return;
                  endDrag();
                }}
                onPointerCancel={(event) => {
                  if (event.pointerType !== "touch") return;
                  endDrag();
                }}
              >
                <span className="mx-auto block h-1.5 w-20 rounded-full bg-muted-foreground/40" />
              </button>
            </div>
          )}
          {children}
          <SheetPrimitive.Close
            ref={closeRef}
            data-slot="close"
            className={cn(
              "absolute right-4 top-4 z-10 rounded-sm opacity-70 ring-offset-background transition-opacity data-[state=open]:bg-secondary hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none",
              hideOnMobile && "hidden md:inline-flex",
            )}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </SheetPrimitive.Close>
        </SheetPrimitive.Content>
      </SheetPortal>
    );
  },
);
SheetContent.displayName = SheetPrimitive.Content.displayName;

const SheetHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col space-y-2 text-center sm:text-left", className)} {...props} />
);
SheetHeader.displayName = "SheetHeader";

const SheetFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)} {...props} />
);
SheetFooter.displayName = "SheetFooter";

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title ref={ref} className={cn("text-lg font-semibold text-foreground", className)} {...props} />
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
};
